import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, Zap, Globe2, CheckCircle2, Mail, Users, TrendingUp, Server } from 'lucide-react';

const STATS = [
  { value: '99.9%', label: 'Uptime', icon: TrendingUp, color: 'text-emerald-400' },
  { value: '12ms', label: 'Latency', icon: Zap, color: 'text-amber-400' },
  { value: '50K+', label: 'İstifadəçi', icon: Users, color: 'text-primary-400' },
  { value: 'AES-256', label: 'Şifrələmə', icon: Shield, color: 'text-rose-400' },
];

const EMAILS = [
  { from: 'admin@emailaze.com', subject: 'Yeni hesab aktivasiyası', time: '09:41', read: false },
  { from: 'security@emailaze.com', subject: '2FA doğrulama kodu', time: '09:38', read: false },
  { from: 'noreply@emailaze.com', subject: 'Aylıq hesabat hazırdır', time: '09:12', read: true },
  { from: 'support@emailaze.com', subject: 'Biletiniz həll edildi', time: 'Dünən', read: true },
];

export default function HeroSection() {
  const { t } = useTranslation();
  const [activeEmail, setActiveEmail] = useState(0);
  const [deliveryCount, setDeliveryCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveEmail(prev => (prev + 1) % EMAILS.length);
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let count = 0;
    const target = 2847;
    const step = Math.ceil(target / 60);
    const timer = setInterval(() => {
      count = Math.min(count + step, target);
      setDeliveryCount(count);
      if (count >= target) clearInterval(timer);
    }, 24);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Overlays */}
      <div className="absolute inset-0 bg-slate-900/80" />
      <div className="absolute inset-0 bg-gradient-to-br from-primary-950/70 via-slate-900/30 to-slate-900/80" />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-slate-900/60 to-transparent" />

      {/* Animated grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'linear-gradient(rgba(99,102,241,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.8) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Glow orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-600/15 rounded-full blur-3xl pointer-events-none animate-float-slow" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" style={{ animation: 'floatSlow 8s ease-in-out infinite reverse' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* Left — Text */}
          <div>
            {/* Badge */}
            <div className="animate-fade-in-up delay-100 inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-8">
              <span className="relative flex">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="absolute inset-0 w-2 h-2 rounded-full bg-emerald-400 animate-ping opacity-60" />
              </span>
              <span className="text-white/90 text-sm font-medium">{t('home.hero_badge')}</span>
            </div>

            {/* Title */}
            <h1 className="font-display text-5xl sm:text-6xl font-bold leading-tight mb-6 drop-shadow-lg">
              <span className="animate-fade-in-up delay-200 block text-white/80 font-light">
                {t('home.hero_title_1')}
              </span>
              <span className="animate-fade-in-up delay-300 block text-white">
                {t('home.hero_title_2')}
              </span>
              <span className="animate-fade-in-up delay-400 block animate-shimmer-text">
                {t('home.hero_title_3')}
              </span>
            </h1>

            {/* Subtitle */}
            <p className="animate-fade-in-up delay-500 text-white/60 text-lg max-w-xl mb-10 leading-relaxed">
              {t('home.hero_subtitle')}
            </p>

            {/* CTAs */}
            <div className="animate-fade-in-up delay-600 flex flex-col sm:flex-row gap-4 mb-12">
              <Link
                to="/register"
                className="group relative inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-400 text-white font-semibold px-8 py-4 rounded-full transition-all duration-200 shadow-lg shadow-primary-900/40 hover:shadow-xl hover:shadow-primary-800/50 text-base overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-primary-400/0 via-white/10 to-primary-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                {t('home.hero_cta')}
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 border border-white/30 bg-white/5 backdrop-blur-sm text-white hover:bg-white/15 font-semibold px-8 py-4 rounded-full transition-all duration-200 text-base"
              >
                {t('home.hero_login')}
              </Link>
            </div>

            {/* Trust chips */}
            <div className="animate-fade-in-up delay-700 flex flex-wrap gap-3">
              {[
                { icon: Shield, label: t('home.trust1'), color: 'text-emerald-400' },
                { icon: Zap, label: t('home.trust2'), color: 'text-amber-400' },
                { icon: Globe2, label: t('home.trust3'), color: 'text-primary-400' },
              ].map(({ icon: Icon, label, color }) => (
                <div key={label} className="flex items-center gap-2 bg-white/5 border border-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                  <Icon size={14} className={color} />
                  <span className="text-white/70 text-sm font-medium">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Animated Dashboard Mockup */}
          <div className="hidden lg:flex justify-end animate-fade-in-right delay-300">
            <div className="relative w-full max-w-md">

              {/* Floating background glow */}
              <div className="absolute -inset-4 bg-primary-500/10 rounded-3xl blur-2xl" />

              {/* Main card */}
              <div className="relative bg-slate-900/90 border border-slate-700/60 rounded-2xl overflow-hidden backdrop-blur-sm shadow-2xl animate-float-slow">

                {/* Card header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-950/50">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-rose-500" />
                      <span className="w-3 h-3 rounded-full bg-amber-500" />
                      <span className="w-3 h-3 rounded-full bg-emerald-500" />
                    </div>
                    <span className="text-slate-500 text-xs font-mono ml-1">emailaze — inbox</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="relative flex">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    </span>
                    <span className="text-emerald-500 text-xs font-mono">Live</span>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-4 gap-0 border-b border-slate-800">
                  {STATS.map(({ value, label, icon: Icon, color }, i) => (
                    <div
                      key={label}
                      className={`px-3 py-3 text-center ${i < 3 ? 'border-r border-slate-800' : ''}`}
                      style={{ animation: `fadeInUp 0.5s ease forwards ${0.4 + i * 0.1}s`, opacity: 0 }}
                    >
                      <Icon size={12} className={`${color} mx-auto mb-1`} />
                      <div className={`text-xs font-bold font-mono ${color}`}>{value}</div>
                      <div className="text-slate-600 text-[10px]">{label}</div>
                    </div>
                  ))}
                </div>

                {/* Email list */}
                <div className="divide-y divide-slate-800/60">
                  {EMAILS.map((email, i) => (
                    <div
                      key={i}
                      className={`flex items-start gap-3 px-4 py-3 transition-all duration-500 ${activeEmail === i ? 'bg-primary-600/10 border-l-2 border-l-primary-500' : 'border-l-2 border-l-transparent hover:bg-slate-800/30'}`}
                      style={{ animation: `fadeInUp 0.5s ease forwards ${0.6 + i * 0.1}s`, opacity: 0 }}
                    >
                      <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${email.read ? 'bg-slate-700' : 'bg-primary-500'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-slate-400 text-xs font-mono truncate">{email.from}</span>
                          <span className="text-slate-600 text-[10px] flex-shrink-0 ml-2">{email.time}</span>
                        </div>
                        <div className={`text-xs truncate ${email.read ? 'text-slate-500' : 'text-slate-200 font-medium'}`}>
                          {email.subject}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Delivery ticker */}
                <div className="px-4 py-3 border-t border-slate-800 bg-slate-950/40 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail size={12} className="text-primary-400" />
                    <span className="text-slate-500 text-xs">Bu gün çatdırıldı</span>
                  </div>
                  <span className="text-primary-400 font-mono font-bold text-sm tabular-nums">
                    {deliveryCount.toLocaleString()}
                  </span>
                </div>

                {/* Scan line effect */}
                <div
                  className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/40 to-transparent pointer-events-none"
                  style={{ animation: 'scanLine 4s linear infinite' }}
                />
              </div>

              {/* Floating badge cards */}
              <div
                className="absolute -left-8 top-1/4 bg-slate-900/95 border border-slate-700 rounded-xl px-3 py-2.5 shadow-xl backdrop-blur-sm"
                style={{ animation: 'float 5s ease-in-out infinite 1s' }}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-emerald-400" />
                  <div>
                    <div className="text-white text-xs font-semibold">SSL Aktiv</div>
                    <div className="text-emerald-500 text-[10px]">TLS 1.3</div>
                  </div>
                </div>
              </div>

              <div
                className="absolute -right-6 bottom-1/3 bg-slate-900/95 border border-slate-700 rounded-xl px-3 py-2.5 shadow-xl backdrop-blur-sm"
                style={{ animation: 'float 6s ease-in-out infinite 0.5s' }}
              >
                <div className="flex items-center gap-2">
                  <Server size={14} className="text-primary-400" />
                  <div>
                    <div className="text-white text-xs font-semibold">DC-1 Bakı</div>
                    <div className="text-primary-400 text-[10px]">Online</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Server status bar */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-black/40 backdrop-blur-sm border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-center gap-6 sm:gap-10">
          {[
            { dot: 'bg-emerald-400', label: 'Bakı DC-1', status: 'Online' },
            { dot: 'bg-emerald-400', label: 'Bakı DC-2', status: 'Online' },
            { dot: 'bg-emerald-400', label: 'CDN', status: 'Active' },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot} animate-pulse`} />
              <span className="text-white/40 text-xs font-mono">{s.label}</span>
              <span className="text-emerald-400 text-xs font-mono">{s.status}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
